import { Handle, Position } from '@xyflow/react';
import { Stack, TextInput, NumberInput, Switch, Select } from '@mantine/core';
import { IconPhoneOutgoing } from '@tabler/icons-react';
import { WidgetNodeShell } from '../../components/flow-editor/nodes/widget-node-shell';
import { LabeledHandle } from '../../components/flow-editor/nodes/labeled-handle';
import type { WidgetDefinition, WidgetNodeProps, ConfigPanelProps } from '../types';
import { transferWidgetCore, type TransferConfig } from '@legacy-shared/widgets/core/transfer';

export { type TransferConfig } from '@legacy-shared/widgets/core/transfer';

const ringToneOptions = [
  {
    group: 'Americas',
    items: [
      { value: 'us', label: 'US (Default)' },
      { value: 'us-old', label: 'US (Old)' },
      { value: 'br', label: 'Brazil' },
      { value: 'cl', label: 'Chile' },
      { value: 'mx', label: 'Mexico' },
      { value: 've', label: 'Venezuela' },
    ],
  },
  {
    group: 'Europe',
    items: [
      { value: 'uk', label: 'UK' },
      { value: 'at', label: 'Austria' },
      { value: 'be', label: 'Belgium' },
      { value: 'bg', label: 'Bulgaria' },
      { value: 'ch', label: 'Switzerland' },
      { value: 'cz', label: 'Czech Republic' },
      { value: 'de', label: 'Germany' },
      { value: 'dk', label: 'Denmark' },
      { value: 'ee', label: 'Estonia' },
      { value: 'es', label: 'Spain' },
      { value: 'fi', label: 'Finland' },
      { value: 'fr', label: 'France' },
      { value: 'gr', label: 'Greece' },
      { value: 'hu', label: 'Hungary' },
      { value: 'it', label: 'Italy' },
      { value: 'lt', label: 'Lithuania' },
      { value: 'nl', label: 'Netherlands' },
      { value: 'no', label: 'Norway' },
      { value: 'pl', label: 'Poland' },
      { value: 'pt', label: 'Portugal' },
      { value: 'ru', label: 'Russia' },
      { value: 'se', label: 'Sweden' },
    ],
  },
  {
    group: 'Asia Pacific',
    items: [
      { value: 'au', label: 'Australia' },
      { value: 'cn', label: 'China' },
      { value: 'in', label: 'India' },
      { value: 'jp', label: 'Japan' },
      { value: 'my', label: 'Malaysia' },
      { value: 'nz', label: 'New Zealand' },
      { value: 'ph', label: 'Philippines' },
      { value: 'sg', label: 'Singapore' },
      { value: 'tw', label: 'Taiwan' },
      { value: 'th', label: 'Thailand' },
    ],
  },
  {
    group: 'Middle East & Africa',
    items: [
      { value: 'il', label: 'Israel' },
      { value: 'za', label: 'South Africa' },
    ],
  },
];

function TransferNode({ id, data, selected }: WidgetNodeProps<TransferConfig>) {
  const preview = data.config.destination || 'No destination set';

  return (
    <WidgetNodeShell
      icon={IconPhoneOutgoing}
      title={data.label || 'Transfer'}
      subtitle={preview}
      configured={!!data.config.destination}
      errors={data.errors}
      selected={selected}
      color="indigo"
    >
      <Handle type="target" position={Position.Top} id="in" />
      <LabeledHandle type="source" position="bottom" id="completed" label="Completed" offset="50%" nodeId={id} />
      <LabeledHandle type="source" position="right" id="failed" label="Failed" offset="50%" nodeId={id} />
    </WidgetNodeShell>
  );
}

function TransferConfigPanel({ config, onChange }: ConfigPanelProps<TransferConfig>) {
  return (
    <Stack gap="md">
      <TextInput
        label="Destination Number"
        description="The phone number to transfer the call to"
        placeholder="+15551234567"
        value={config.destination}
        onChange={(e) => onChange({ ...config, destination: e.target.value })}
      />
      <TextInput
        label="Caller ID"
        description="Phone number shown to the destination (E.164, e.g. +15551234567). Leave empty to pass through the original caller ID."
        placeholder="+15551234567"
        value={config.callerId || ''}
        onChange={(e) => onChange({ ...config, callerId: e.target.value || undefined })}
      />
      <NumberInput
        label="Timeout (seconds)"
        description="How long to wait for the destination to answer"
        value={config.timeout}
        onChange={(value) => onChange({ ...config, timeout: typeof value === 'number' ? value : 30 })}
        min={5}
        max={120}
      />
      <Switch
        label="Answer on Bridge"
        description="Wait until the destination answers before bridging the calls"
        checked={config.answerOnBridge}
        onChange={(e) => onChange({ ...config, answerOnBridge: e.currentTarget.checked })}
      />
      <Select
        label="Ringback Tone"
        description="The regional ringback tone played to the caller while waiting for the transfer to connect"
        value={config.ringTone || 'us'}
        onChange={(value) => onChange({ ...config, ringTone: value === 'us' ? undefined : value || undefined })}
        data={ringToneOptions}
        searchable
      />
      <Switch
        label="Retry with called number if blocked"
        description="If the transfer is rejected by the destination's call screening (SIP 607/608) or blocked by the carrier before dialing (e.g. caller ID reputation blocks like Telnyx D54), re-dial once using this number (the called number) as caller ID. On by default; the destination won't see the original caller's number on the retry."
        checked={config.retryWithCalledNumberOnBlock ?? true}
        onChange={(e) => onChange({ ...config, retryWithCalledNumberOnBlock: e.currentTarget.checked })}
      />
    </Stack>
  );
}

export const transferWidget: WidgetDefinition<TransferConfig> = {
  ...transferWidgetCore,
  icon: 'IconPhoneOutgoing',

  handles: {
    inputs: [{ id: 'in', type: 'default', label: 'Input', position: 'top' }],
    outputs: [
      { id: 'completed', type: 'success', label: 'Completed', position: 'bottom' },
      { id: 'failed', type: 'failure', label: 'Failed', position: 'right' },
    ],
  },

  connectionRules: {
    allowedInputFrom: ['*'],
    allowedOutputTo: ['*'],
  },

  ui: {
    component: TransferNode,
    configPanel: TransferConfigPanel,
    preview: (config) => config.destination || 'No destination',
  },

  validation: {
    ...transferWidgetCore.validation,
    validateConnections: () => ({ valid: true, errors: [] }),
  },
};
